import { useProjectStore } from '@/core/project/store/project.store'
import { SceneNode } from '@/features/screenplay/components/editor/nodes/SceneNode'
import { create } from 'zustand'
import { shotListRepository } from '../repository/ShotListRepository'

export type ShotType = 'Wide' | 'Medium' | 'Close-up' | 'Extreme Close-up' | 'Over the Shoulder' | 'POV' | 'Aerial' | 'Low Angle' | 'High Angle' | 'Dutch Angle' | 'Establishing' | 'Master Shot' | 'Insert' | 'Other'
export type CameraMovement = 'Static' | 'Pan' | 'Tilt' | 'Dolly' | 'Zoom' | 'Tracking' | 'Handheld' | 'Crane' | 'Steadicam' | 'Gimbal' | 'Whip Pan' | 'Other'
export type CameraAngle = 'Eye Level' | 'High Angle' | 'Low Angle' | 'Bird\'s Eye' | 'Worm\'s Eye' | 'Canted/Dutch' | 'Overhead' | 'Other'
export type Framing = 'Single' | 'Two-Shot' | 'Three-Shot' | 'Group Shot' | 'Over the Shoulder' | 'Crowd' | 'Other'
export type ShotPriority = 'Must Have' | 'Good to Have' | 'Optional'

export interface Shot {
    id: string
    description: string
    shotType: ShotType
    cameraMovement: CameraMovement
    cameraAngle?: CameraAngle
    framing?: Framing
    subject?: string
    emotionalPurpose?: string
    duration?: string
    shotPriority?: ShotPriority
}

export interface ShotListState {
    shotLists: Record<string, Shot[]> // Key is sceneId
    isGenerating: boolean
    showScenePreview: boolean
    setShowScenePreview: (isScenePreviewPanelOpen: boolean) => void
    loadShotList: (sceneId: string) => Promise<void>
    selectedSceneNode: SceneNode | null
    setSelectedSceneNode: (sceneNode: SceneNode) => void
    setShotList: (sceneId: string, shots: Shot[]) => void
    updateShot: (sceneId: string, shotId: string, updatedShot: Partial<Shot>) => void
    addShot: (sceneId: string, shot: Shot) => void
    removeShot: (sceneId: string, shotId: string) => void
    setIsGenerating: (isGenerating: boolean) => void
}

export const useShotListStore = create<ShotListState>((set, get) => ({
    shotLists: {},
    selectedSceneNode: null,
    isGenerating: false,
    showScenePreview: false,
    setShowScenePreview: (isScenePreviewPanelOpen: boolean) => set({ showScenePreview: isScenePreviewPanelOpen }),
    setSelectedSceneNode: (sceneNode: SceneNode) => set({ selectedSceneNode: sceneNode }),
    loadShotList: async (sceneId) => {
        const { project } = useProjectStore.getState()
        const projectId = project?.id
        console.log("Loading shot list for scene:", sceneId, "Project:", projectId)
        if (!projectId) {
            console.warn("Cannot load shot list: No projectId set in store", { sceneId })
            return
        }

        console.log("Loading shot list from Repository for scene:", sceneId)
        try {
            const entity = await shotListRepository.getBySceneId(projectId, sceneId)

            if (entity) {
                const shots = entity?.shots || []
                set((state) => ({
                    shotLists: { ...state.shotLists, [sceneId]: shots }
                }))
            }
        } catch (error) {
            console.error("Failed to load shot list:", error)
        }
    },

    setShotList: (sceneId, shots) => {
        set((state) => ({
            shotLists: { ...state.shotLists, [sceneId]: shots }
        }))
        // Persist via Repository
        const { project } = useProjectStore.getState()
        const projectId = project?.id
        if (projectId) {
            shotListRepository.saveShots(projectId, sceneId, shots).catch(err =>
                console.error("Error saving shot list:", err)
            )
        } else {
            console.error("Failed to save shot list: No projectId set")
        }
    },

    updateShot: (sceneId, shotId, updatedShot) => {
        const { shotLists } = get()

        const { project } = useProjectStore.getState()
        const projectId = project?.id
        const currentList = shotLists[sceneId] || []
        const updatedList = currentList.map(shot =>
            shot.id === shotId ? { ...shot, ...updatedShot } : shot
        )

        set((state) => ({ shotLists: { ...state.shotLists, [sceneId]: updatedList } }))

        if (projectId) {
            shotListRepository.saveShots(projectId, sceneId, updatedList).catch(err =>
                console.error("Error saving updated shot list:", err)
            )
        } else {
            console.error("Failed to update shot: No projectId set")
        }
    },

    addShot: (sceneId, shot) => {
        const { shotLists } = get()

        const { project } = useProjectStore.getState()
        const projectId = project?.id
        const updatedList = [...(shotLists[sceneId] || []), shot]

        set((state) => ({
            shotLists: { ...state.shotLists, [sceneId]: updatedList }
        }))

        if (projectId) {
            console.log("Saving new shot to DB...", { projectId, sceneId })
            shotListRepository.saveShots(projectId, sceneId, updatedList).catch(err =>
                console.error("Error saving new shot:", err)
            )
        } else {
            console.error("Failed to add shot: No projectId set")
        }
    },

    removeShot: (sceneId, shotId) => {
        const { shotLists } = get()
        const { project } = useProjectStore.getState()
        const projectId = project?.id
        const updatedList = (shotLists[sceneId] || []).filter(s => s.id !== shotId)

        set((state) => ({
            shotLists: { ...state.shotLists, [sceneId]: updatedList }
        }))

        if (projectId) {
            shotListRepository.saveShots(projectId, sceneId, updatedList).catch(err =>
                console.error("Error removing shot:", err)
            )
        } else {
            console.error("Failed to remove shot: No projectId set")
        }
    },

    setIsGenerating: (isGenerating) => set({ isGenerating }),
}))
